import React, { useRef } from 'react';
import SearchBarSegment from './SearchBarSegment';
import EmailSegment from './EmailSegment';
import PhoneSegment from './PhoneSegment';
import DesktopPanel from './DesktopPanel';
import { FiMapPin, FiCalendar, FiUsers, FiShield, FiTag, FiMic } from 'react-icons/fi';
import { FaCalculator } from 'react-icons/fa';
import './SearchBar.css';

const SearchBar = ({
  // Insurance
  selectedInsurance,
  products,
  // Origin
  showOrigin,
  originSummary,
  // Destination
  showDestination,
  destinationSummary,
  // Product
  showProduct,
  productSummary,
  // Dates
  showDates,
  dateSummary,
  // Passengers
  passengersSummary,
  // Contact/Email/Phone
  showContact,
  showEmail,
  showPhone,
  email,
  onEmailChange,
  emailError,
  phone,
  onPhoneChange,
  phoneError,
  dialCountryCode,
  onDialCountryCodeChange,
  // Validation errors
  destinoError,
  viajerosError,
  fechasError,
  productoError,
  // Voice
  showVoice,
  onVoiceClick,
  // Active panel
  activePanel,
  onPanelChange,
  // Search
  onSearch,
  // Panel content
  renderInsurancePanel,
  renderOriginPanel,
  renderDestinationPanel,
  renderProductPanel,
  renderDatesPanel,
  renderPassengersPanel,
  // renderContactPanel removed - email always visible in searchbar
}) => {
  const searchbarRef = useRef(null);
  const insuranceRef = useRef(null);
  const originRef = useRef(null);
  const destinationRef = useRef(null);
  const productRef = useRef(null);
  const datesRef = useRef(null);
  const passengersRef = useRef(null);

  const togglePanel = (panel) => {
    onPanelChange(activePanel === panel ? null : panel);
  };

  return (
    <div className="sf-searchbar-wrapper">
      <p className="sf-searchbar-title">
        Cotiza desde <span className="sf-searchbar-title__accent">0.99 USD</span> por día
      </p>
      <div className="sf-searchbar" ref={searchbarRef}>
        {/* Insurance Type Segment */}
        <SearchBarSegment
          ref={insuranceRef}
          label="Tipo de Seguro"
          value={selectedInsurance?.name?.replace('Multiples', 'Múltiples')}
          placeholder="Seleccionar"
          isActive={activePanel === 'insurance'}
          onClick={() => togglePanel('insurance')}
          icon={<FiShield />}
        />

        <div className="sf-searchbar__divider" />

        {/* Origin Segment */}
        {showOrigin && (
          <>
            <SearchBarSegment
              ref={originRef}
              label="Origen"
              value={originSummary}
              placeholder="¿Desde dónde?"
              isActive={activePanel === 'origin'}
              onClick={() => togglePanel('origin')}
              icon={<FiMapPin />}
              hasError={false}
            />
            <div className="sf-searchbar__divider" />
          </>
        )}

        {/* Destination Segment */}
        {showDestination && (
          <>
            <SearchBarSegment
              ref={destinationRef}
              label="Destino"
              value={destinationSummary}
              placeholder="¿A dónde?"
              isActive={activePanel === 'destination'}
              onClick={() => togglePanel('destination')}
              icon={<FiMapPin />}
              hasError={destinoError}
            />
            <div className="sf-searchbar__divider" />
          </>
        )}

        {/* Product Segment */}
        {showProduct && (
          <>
            <SearchBarSegment
              ref={productRef}
              label="Producto"
              value={productSummary}
              placeholder="Seleccionar"
              isActive={activePanel === 'product'}
              onClick={() => togglePanel('product')}
              icon={<FiTag />}
              hasError={productoError}
            />
            <div className="sf-searchbar__divider" />
          </>
        )}

        {/* Dates Segment */}
        {showDates && (
          <>
            <SearchBarSegment
              ref={datesRef}
              label="Fechas"
              value={dateSummary}
              placeholder="Agregar fechas"
              isActive={activePanel === 'dates'}
              onClick={() => togglePanel('dates')}
              icon={<FiCalendar />}
              hasError={fechasError}
              className="sf-searchbar__segment--dates"
            />
            <div className="sf-searchbar__divider" />
          </>
        )}

        {/* Passengers Segment */}
        <SearchBarSegment
          ref={passengersRef}
          label="Viajeros"
          value={passengersSummary}
          placeholder="Agregar viajeros"
          isActive={activePanel === 'passengers'}
          onClick={() => togglePanel('passengers')}
          icon={<FiUsers />}
          hasError={viajerosError}
          className="sf-searchbar__segment--passengers"
        />

        {/* Voice Input Icon */}
        {showVoice && (
          <>
            <div className="sf-searchbar__divider" />
            <button
              type="button"
              className="sf-searchbar__voice-btn"
              onClick={onVoiceClick}
              title="Entrada por voz"
            >
              <FiMic />
            </button>
          </>
        )}

        <div className="sf-searchbar__divider" />

        {/* Email Field */}
        {showEmail && (
          <>
            <EmailSegment
              email={email}
              onEmailChange={onEmailChange}
              emailError={emailError}
              onFocus={() => onPanelChange(null)}
            />
            <div className="sf-searchbar__divider" />
          </>
        )}

        {/* Phone Field */}
        {showPhone && (
          <>
            <PhoneSegment
              phone={phone}
              onPhoneChange={onPhoneChange}
              phoneError={phoneError}
              onFocus={() => onPanelChange(null)}
              dialCountryCode={dialCountryCode}
              onDialCountryCodeChange={onDialCountryCodeChange}
            />
            <div className="sf-searchbar__divider" />
          </>
        )}

        {/* Search Button */}
        <button
          type="button"
          className="sf-searchbar__cta"
          onClick={onSearch}
        >
          <FaCalculator />
          <span>Cotizar</span>
        </button>
      </div>

      {/* Panels — rendered via Portal to escape overflow:hidden of host page */}
      <DesktopPanel isOpen={activePanel === 'insurance'} onClose={() => onPanelChange(null)} anchorRef={insuranceRef}>
        {renderInsurancePanel && renderInsurancePanel()}
      </DesktopPanel>

      {showOrigin && (
        <DesktopPanel isOpen={activePanel === 'origin'} onClose={() => onPanelChange(null)} anchorRef={originRef}>
          {renderOriginPanel && renderOriginPanel()}
        </DesktopPanel>
      )}

      {showDestination && (
        <DesktopPanel isOpen={activePanel === 'destination'} onClose={() => onPanelChange(null)} anchorRef={destinationRef}>
          {renderDestinationPanel && renderDestinationPanel()}
        </DesktopPanel>
      )}

      {showProduct && (
        <DesktopPanel isOpen={activePanel === 'product'} onClose={() => onPanelChange(null)} anchorRef={productRef}>
          {renderProductPanel && renderProductPanel()}
        </DesktopPanel>
      )}

      {showDates && (
        <DesktopPanel isOpen={activePanel === 'dates'} onClose={() => onPanelChange(null)} align="right" panelId="dates" anchorRef={datesRef}>
          {renderDatesPanel && renderDatesPanel()}
        </DesktopPanel>
      )}

      <DesktopPanel isOpen={activePanel === 'passengers'} onClose={() => onPanelChange(null)} align="right" anchorRef={passengersRef}>
        {renderPassengersPanel && renderPassengersPanel()}
      </DesktopPanel>

      {/* Email panel removed - email is always visible in searchbar */}

      {/* Consent text — solo visible cuando el producto pide email */}
      {showEmail && (
        <div className="sf-searchbar-consent">
          <p className="sf-searchbar-consent__text">
            Al cotizar aceptas recibir comunicaciones de Segurosfly.com de acuerdo con nuestra{' '}
            <a
              href="https://segurosfly.com/politica-privacidad"
              target="_blank"
              rel="noopener noreferrer"
              className="sf-searchbar-consent__link"
            >
              Política de Privacidad
            </a>.
          </p>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
